import {toastr} from 'react-redux-toastr';
import {initialize, formValueSelector} from 'redux-form';
import moment from 'moment';
import {
  getJsonAsync,
  putJsonAsync,
  postJsonAsync
} from 'shared/actions';
import {setErrorList} from 'shared/error/actionTypes';
import {loadTournament} from '../actions';
import {
  displayVirtualEventScoringMethodOptions,
  displayVirtualEventOwnedByOptions,
  getVirtualEventScoringMethods,
  getVirtualEventOwnedByOptions,
  getHandicapAppliedToOptions,
  displayVirtualEventForm,
  cancelVirtualEventForm,
  displayVirtualEvents,
  displayNewVirtualEvent,
  displayNewEvent,
  displayEventForm,
  cancelEventItemForm,
  displayEvents,
  setAgeOptions,
  displayVirtualEventAgeOptions,
  getAvailableLocationOptions
} from './actionTypes';

const defaultAgeAppliesTo = payload => {
  if (payload.ageBased && !payload.ageAppliesTo) {
    payload.ageAppliesTo = "Oldest Member";
  }
};

const defaultScoringMethod = payload => {
  if (!payload.scoringMethod) {
    payload.scoringMethod = "First";
  }
};

export const handleLinkedEventsToggleClick = () => (dispatch, getState) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id;
  const linkEvents = !getState().tournament.base.tournamentInfo.linkEvents;
  const request = {
    tournamentId,
    linkEvents
  };

  dispatch(postJsonAsync(`setup/tournament/${tournamentId}/linkEvents`, request))
  .then(() => {
    toastr.success('Saved!', `Doubles/Singles ${linkEvents ? 'linked' : 'unlinked'} successfully`);
    dispatch(loadTournament(tournamentId));
  })
  .catch(() => {});
};

export const handleVirtualEventItemClick = virtualEventId => (dispatch, getState) => {
  dispatch(getJsonAsync(`virtualevent/${virtualEventId}`))
  .then(data => {
    dispatch(displayVirtualEventForm(virtualEventId));
    data.maps = data.virtualEventMaps.map(virtualEventMap => virtualEventMap.eventId).join(',');
    dispatch(displayScoringMethodOptions(false));
    dispatch(displayOwnedByOptions(false));
   
    if (data.virtualEventMaps.length > 1) {
      const validBowledEvent = getState().tournament.events.bowlEvents.find(x => x.maxEntriesPerBowler > 1);

      if (validBowledEvent != undefined && validBowledEvent != null) {
        data.scoringMethods = dispatch(retrieveScoringMethods());
        dispatch(handleMappedEventsUpdate(data.maps));
        dispatch(displayScoringMethodOptions(true));
      }
    }

    if(data.virtualEventMaps.length === 1){
      const applicableEvents = getState().tournament.events.bowlEvents
        .filter(x => data.maps.includes(x.id));

      if(applicableEvents !== undefined && applicableEvents.length === 1){
        data.ownedByOptions = dispatch(retrieveOwnedByOptions());
        dispatch(handleMappedEventsUpdate(data.maps));
        dispatch(displayOwnedByOptions(true));
      }
    }

    // check for events with more than 1 bowler
    if (data.ageBased) {
      const applicableEvents1 = getState().tournament.events.bowlEvents
        .filter(x => x.numberOfBowlers > 1)
        .filter(x => data.maps.includes(x.id));
    
      if (applicableEvents1 !== undefined && applicableEvents1.length !== 0) {
        dispatch(displayAgeOptions(true));
      }
      else {
        dispatch(displayAgeOptions(false));
      }
    }

    if (data.isHandicapped)
      dispatch(retrieveHandicapAppliedToOptions());

    dispatch(initialize(`virtualEventForm`, data));
  })
  .catch(() => {});
};

export const validateVirtualEventForm = values => {
  let errors = {};
  const fields = [
    'name',
    'expenseFee',
    'lineageFee',
    'prizeFee',
    'handicapPercentage',
    'handicapAverage',
    'handicapApplied'
  ];

  for (let field of fields) {
    let error = {};

    // general validation (required, positive number)
    if (typeof(values[field]) === 'number' && values[field] < 0) {
      error[field] = 'Must be zero or a greater number';
    } else if (typeof(values[field]) !== 'number' && !values[field]) {
      error[field] = 'Required';
    }

    // specific validation
    if (field === 'expenseFee' && values[field] < 0 ) {
      error[field] = 'Cannot be negative amount';
    }

    if (field === 'lineageFee' && values[field] < 0 ) {
      error[field] = 'Cannot be negative amount';
    }

    if (field === 'prizeFee' && values[field] < 0 ) {
      error[field] = 'Cannot be negative amount';
    }

    if (field === 'handicapPercentage' && values[field] > 100) {
      error[field] = 'Cannot be greater than 100';
    }

    if (field === 'handicapPercentage' && values[field] < 0) {
      error[field] = 'Cannot be negative';
    }

    if (field === 'handicapAverage' && values[field] > 300) {
      error[field] = 'Cannot be greater than 300';
    }

    errors = {
      ...errors,
      ...error
    };
  }

  return errors;
};

export const handleSaveVirtualEventForm = formData => (dispatch, getState) => new Promise((resolve) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id;
  const payload = {
    tournamentId,
    ...formData,
    virtualEventMaps: formData.maps ? formData.maps.split(',') : []
  };

  const promise = formData.id
    ? putJsonAsync(`virtualevent`, payload)
    : postJsonAsync(`virtualevent`, payload);

  defaultAgeAppliesTo(payload);
  defaultScoringMethod(payload);
  dispatch(promise)
  .then(() => {
    toastr.success('Saved!', 'Virtual event successfully saved.');
    dispatch(retrieveVirtualEvents(tournamentId));
    dispatch(loadTournament(tournamentId));
    resolve();
  })
  .catch(() => resolve());
});

export const handleCancelVirtualEventForm = () => dispatch => { 
  dispatch(setErrorList([]));
  dispatch(cancelVirtualEventForm());
};

export const handleNewVirtualEventClick = () => dispatch => {
  dispatch(cancelVirtualEventForm());
  dispatch(displayNewVirtualEvent());
  dispatch(retrieveScoringMethods());
  dispatch(retrieveOwnedByOptions());
  dispatch(displayScoringMethodOptions(false));
  dispatch(displayOwnedByOptions(false));
  dispatch(displayVirtualEventAgeOptions(false));
  dispatch(initialize(`virtualEventForm`, {}));
};

export const retrieveVirtualEvents = tournamentId => dispatch => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/virtualevents`))
  .then(virtualEvents => {
    dispatch(displayVirtualEvents(virtualEvents));
    dispatch(getJsonAsync(`virtualevent/${tournamentId}/viewEvents`));
    dispatch(retrieveAgeOptions());
  })
  .catch(() => {});
};

export const retrieveEvent = eventId => dispatch => {
  dispatch(getJsonAsync(`event/${eventId}`))
  .then(data => {
    setTimeout(() => dispatch(initialize(`eventItemForm`, data)));
    dispatch(displayEventForm(data.id));
    dispatch(retrieveAvailableLocationOptions());

    if (data.isHandicapped)
      dispatch(retrieveHandicapAppliedToOptions());
  })
  .catch(() => {});
};

export const handleNewBowlEventClick = () => dispatch => {
  dispatch(cancelEventItemForm());
  dispatch(displayNewEvent());
  dispatch(initialize(`eventItemForm`, {}));
};

export const retrieveEvents = tournamentId => dispatch => {
  dispatch(getJsonAsync(`tournament/${tournamentId}/events`))
  .then(data => {
    dispatch(displayEvents(data));
    dispatch(retrieveAgeOptions());
    dispatch(retrieveAvailableLocationOptions(tournamentId));
  })
  .catch(() => {});
};

export const validateBowlEventForm = values => {
  let errors = {};
  const fields = [
    'name',
    'numberOfBowlers',
    'gamesPerSeries',
    'entriesPerLanePair',
    'maxEntriesPerBowler',
    'handicapPercentage',
    'handicapAverage',
    'handicapApplied',
    'expenseFee',
    'lineageFee',
    'prizeFee',
    'numberOfBowlersThatMustBeDifferentPerTeam',
    'location'
  ];

  for (let field of fields) {
    let error = {};

    // general validation (required, positive number)
    if (typeof(values[field]) === 'number' && values[field] < 0) {
      error[field] = 'Must be zero or a greater number';
    } else if (typeof(values[field]) !== 'number' && !values[field]) {
      error[field] = 'Required';
    }

    // specific validation
    if (field === 'maxEntriesPerBowler' && values[field] <= 0) {
      error[field] = 'Must be greater than zero';
    }

    if (field === 'handicapPercentage' && values[field] > 100) {
      error[field] = 'Cannot be greater than 100';
    }

    if (field === 'handicapPercentage' && values[field] < 0) {
      error[field] = 'Cannot be negative';
    }

    if (field === 'handicapAverage' && values[field] > 300) {
      error[field] = 'Cannot be greater than 300';
    }

    if (field === 'numberOfBowlersThatMustBeDifferentPerTeam' && values[field] > values['numberOfBowlers']) {
      error[field] = 'Cannot be greater than number of bowlers per team';
    }

    errors = {
      ...errors,
      ...error
    };
  }

  return errors;
};

export const handleSaveBowlEventForm = data => (dispatch, getState) => new Promise((resolve) => {
  const tournamentId = getState().tournament.base.tournamentInfo.id;
  const payload = {
    ...data,
    tournamentId
  };

  defaultAgeAppliesTo(payload);
  const promise = data.id
    ? putJsonAsync(`event`, payload)
    : postJsonAsync(`event`, payload);

  dispatch(promise)
  .then(() => {
    toastr.success('Saved!', 'Tournament Event saved Successfully.');
    dispatch(retrieveEvents(tournamentId));
    dispatch(loadTournament(tournamentId));
    resolve();
  })
  .catch(() => resolve());
});

export const handleDeleteBowlEventForm = () => (dispatch, getState) => {
  const eventId = getState().form.eventItemForm 
    ? getState().form.eventItemForm.values.id 
    : getState().form.virtualEventForm.values.id;

  dispatch(postJsonAsync(`event/${eventId}/remove`))
    .then(response => {
      const errorMessage = response;
      
      if (errorMessage && errorMessage.length > 0) {
        toastr.error('Event Not Removed', errorMessage);
      }
      else {
        toastr.success('Saved!', 'Event Removed Successfully.');
      }
    })
    .catch(error => {
      toastr.error('Error removing the event!', error.toString());
    });
};

export const handleAgeVerificationDateChanged = date => (dispatch, getState) => new Promise((resolve) => {
  const ageVerificationDate = getState().tournament.base.tournamentInfo.ageVerificationDate;
  const ageVerificationDateFormatted = ageVerificationDate !== null ? moment.parseZone(ageVerificationDate).format(`YYYY-MM-DD`): ageVerificationDate;
  const formattedDate = date !== null ? moment.parseZone(date).format(`YYYY-MM-DD`): null;
  
  if (ageVerificationDateFormatted !== formattedDate) {
    const tournamentId = getState().tournament.base.tournamentInfo.id;
    const payload = {
      ageVerificationDate: formattedDate,
      tournamentId
    };

    dispatch(postJsonAsync(`setup/tournament/${tournamentId}/ageVerificationDate`, payload))
    .then(() => {
      toastr.success('Saved!', 'Age Verification Date saved successfully.');
      dispatch(loadTournament(tournamentId));
    })
    .catch(() => resolve());
  }
});

export const handleCancelBowlEventForm = () => dispatch => {
  dispatch(setErrorList([]));
  dispatch(cancelEventItemForm());
};

export const retrieveAgeOptions = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const ageOptions = getState().tournament.events.ageOptions;

  // prevent going to server if ageOptions already in state
  if (ageOptions && ageOptions.length) {
    resolve(ageOptions);
  }

  dispatch(getJsonAsync(`division/ageOptions`))
  .then(ageOptions => {
    dispatch(setAgeOptions(ageOptions.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    resolve(ageOptions);
  })
  .catch(()=>reject());
});

export const displayScoringMethodOptions = showScoringMethods => dispatch => {
  dispatch(displayVirtualEventScoringMethodOptions(showScoringMethods));
};

export const displayOwnedByOptions = showOwnedBy => dispatch => {
  dispatch(displayVirtualEventOwnedByOptions(showOwnedBy));
};

export const displayAgeOptions = showAgeOptions => dispatch => {
  dispatch(displayVirtualEventAgeOptions(showAgeOptions));
};

export const retrieveScoringMethods = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const scoringMethods = getState().tournament.events.scoringMethods;

  // prevent going to server if scoringMethods already in state
  if (scoringMethods && scoringMethods.length) {
    resolve(scoringMethods);
  }

  dispatch(getJsonAsync(`setup/tournament/scoringMethods`))
  .then(scoringMethods => {
    dispatch(getVirtualEventScoringMethods(scoringMethods.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    resolve(scoringMethods);
  })
  .catch(()=>reject());
});

export const retrieveOwnedByOptions = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const ownedByOptions = getState().tournament.events.ownedByOptions;

  // prevent going to server if ownedByOptions already in state
  if (ownedByOptions && ownedByOptions.length) {
    resolve(ownedByOptions);
  }

  dispatch(getJsonAsync(`setup/tournament/ownedByOptions`))
  .then(ownedByOptions => {
    dispatch(getVirtualEventOwnedByOptions(ownedByOptions.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    resolve(ownedByOptions);
  })
  .catch(()=>reject());
});

export const handleMappedEventsUpdate = fieldData => (dispatch, getState) => {
  const virtualEventMaps = fieldData ? fieldData.split(',') : [];
  const selector = formValueSelector('virtualEventForm');
  const ageBased = selector(getState(), 'ageBased');

  dispatch(displayScoringMethodOptions(false));
  dispatch(displayOwnedByOptions(false));

  if (virtualEventMaps.length > 1) {
    const applicableEvents2 = getState().tournament.events.bowlEvents
      .filter(x => x.maxEntriesPerBowler > 1)
      .filter(x => virtualEventMaps.includes(x.id));

    if (applicableEvents2 !== undefined && applicableEvents2.length !== 0) {
      dispatch(displayScoringMethodOptions(true));
    }
  }
  if(virtualEventMaps.length === 1){
    const applicableEvents3 = getState().tournament.events.bowlEvents
      .filter(x => virtualEventMaps.includes(x.id));

    if(applicableEvents3 !== undefined && applicableEvents3.length === 1){
      dispatch(displayOwnedByOptions(true));
    }
  }
  // check for events with more than 1 bowler
  if (ageBased) {
    const eventsWithMultipleBowlers = getState().tournament.events.bowlEvents
      .filter(x => x.numberOfBowlers > 1)
      .filter(x => virtualEventMaps.includes(x.id));
  
    if (eventsWithMultipleBowlers !== undefined && eventsWithMultipleBowlers.length !== 0) {
      dispatch(displayAgeOptions(true));
    }
    else {
      dispatch(displayAgeOptions(false));
    }
  }
};

export const handleAgeBasedClick = (checked) => (dispatch, getState) => {
  dispatch(displayAgeOptions(false));
  const selector = formValueSelector('virtualEventForm');
  const linkedEventMaps = selector(getState(), 'maps');

  if (checked) {
    if (linkedEventMaps !== undefined) {      
      const virtualEventMaps = linkedEventMaps ? linkedEventMaps.split(',') : [];

      // check number of bowlers from linked event.  If > 1, then show AgeOptions
      if (virtualEventMaps.length > 0) {
          const applicableEvents3 = getState().tournament.events.bowlEvents
            .filter(x => x.numberOfBowlers > 1)
            .filter(x => virtualEventMaps.includes(x.id));

        if (applicableEvents3 !== undefined && applicableEvents3.length !== 0) {
          dispatch(displayAgeOptions(true));
        }
      }
    }
  }
};

export const handleIsHideFromCaptainClick = (tournamentId, eventId, checked) => (dispatch) => {
  dispatch(putJsonAsync(`virtualevent/${eventId}/hideFromCaptains`, {TournamentId: tournamentId, VirtualEventId: eventId, HideFromCaptains: checked}))
  .then(() => toastr.success('Saved!', 'Virtual event successfully saved.'));
  dispatch(retrieveVirtualEvents(tournamentId));
};

export const handleIsHandicappedClick = (checked) => (dispatch) => {
  if (checked) {
     dispatch(retrieveHandicapAppliedToOptions());
  }
};

export const retrieveHandicapAppliedToOptions = () => (dispatch, getState) => new Promise((resolve, reject) => {
  const handicapAppliedOptions = getState().tournament.events.handicapAppliedOptions;

  // prevent going to server if options already in state
  if (handicapAppliedOptions && handicapAppliedOptions.length) {
    resolve(handicapAppliedOptions);
  }

  dispatch(getJsonAsync(`setup/tournament/handicapAppliedTo`))
  .then(handicapAppliedOptions => {
    dispatch(getHandicapAppliedToOptions(handicapAppliedOptions.map(({id, name}) => ({
      value: id,
      label: name
    }))));
    resolve(handicapAppliedOptions);
  })
  .catch(()=>reject());
});

export const retrieveAvailableLocationOptions = (tournamentId) => (dispatch, getState) => {
  if (!tournamentId) tournamentId = getState().tournament.base.tournamentInfo.id;

  dispatch(getJsonAsync(`setupEvents/${tournamentId}/eventLocations`))
  .then(availableLocationOptions => {
    dispatch(getAvailableLocationOptions(availableLocationOptions.map(({id, name, certificationNumber}) => ({
      value: id,
      label: `${name} (#${certificationNumber})`
    }))));
  });
};



// WEBPACK FOOTER //
// ./src/features/tournament/events/actions.js